"use client";

import {
  createContext,
  createElement,
  useContext,
  useMemo,
  type ComponentType,
  type ReactNode,
} from "react";

import {
  isPhiRuntimeDataProviderKey,
  type PhiRuntimeDataProviderKey,
} from "../../../../types/runtime-data-provider";
import type {
  PhiTableProviderMutationRequest,
  PhiTableProviderMutationResult,
  PhiTableProviderRecordRequest,
  PhiTableProviderQueryRequest,
  PhiTableProviderQueryResult,
  PhiTableProviderResourceDescriptor,
} from "../../../../types/table-widget";
import type { PhiProviderResourceSource } from "../../../../types/runtime-data-provider";

export type PhiTableProviderRegistration = {
  key: PhiRuntimeDataProviderKey;
  resources: readonly PhiTableProviderResourceDescriptor[];
  query: (request: PhiTableProviderQueryRequest) => Promise<PhiTableProviderQueryResult>;
  readRecord?: (request: PhiTableProviderRecordRequest) => Promise<Record<string, unknown>>;
  mutate?: (request: PhiTableProviderMutationRequest) => Promise<PhiTableProviderMutationResult>;
};

const EMPTY_TABLE_PROVIDER_REGISTRY = new Map<
  PhiRuntimeDataProviderKey,
  PhiTableProviderRegistration
>();
const PhiTableProviderRegistryContext = createContext<
  ReadonlyMap<PhiRuntimeDataProviderKey, PhiTableProviderRegistration>
>(EMPTY_TABLE_PROVIDER_REGISTRY);

export function createPhiTableProviderClient(
  registration: PhiTableProviderRegistration,
): ComponentType<{ children: ReactNode }> {
  if (!isPhiRuntimeDataProviderKey(registration.key)) {
    throw new Error(`Invalid table provider key "${registration.key}".`);
  }
  assertPhiTableProviderRegistration(registration);

  return function PhiBoundTableProviderClient({ children }) {
    return (
      <PhiTableProviderClient registration={registration}>
        {children}
      </PhiTableProviderClient>
    );
  };
}

export function PhiTableProviderClient({
  registration,
  children,
}: {
  registration: PhiTableProviderRegistration;
  children: ReactNode;
}) {
  const parent = useContext(PhiTableProviderRegistryContext);
  const registry = useMemo(() => {
    if (!isPhiRuntimeDataProviderKey(registration.key)) {
      throw new Error(`Invalid table provider key "${registration.key}".`);
    }
    assertPhiTableProviderRegistration(registration);
    if (parent.has(registration.key)) {
      throw new Error(`Duplicate active table provider "${registration.key}".`);
    }
    const next = new Map(parent);
    next.set(registration.key, registration);
    return next;
  }, [parent, registration]);

  return createElement(PhiTableProviderRegistryContext.Provider, { value: registry }, children);
}

export function PhiTableProviderIsolationBoundary({ children }: { children: ReactNode }) {
  return createElement(
    PhiTableProviderRegistryContext.Provider,
    { value: EMPTY_TABLE_PROVIDER_REGISTRY },
    children,
  );
}

function assertPhiTableProviderRegistration(registration: PhiTableProviderRegistration) {
  if (registration.resources.length === 0) {
    throw new Error(`Table provider "${registration.key}" has no resources.`);
  }
  const keys = new Set<string>();
  for (const resource of registration.resources) {
    if (!resource.resourceKey.trim() || keys.has(resource.resourceKey)) {
      throw new Error(`Table provider "${registration.key}" has an empty or duplicate resource key.`);
    }
    keys.add(resource.resourceKey);
    /*
     * A capability that answers nothing is worse than one that was never offered: the record Widget
     * binds, the author sees a Provider that says it can do this, and the failure arrives later and
     * reads like missing data. The static Table Provider carried exactly that shape until the Widget
     * asked, so the registration says it here instead.
     */
    if (resource.recordRead === true && !registration.readRecord) {
      throw new Error(
        `Table provider "${registration.key}" declares recordRead on resource "${resource.resourceKey}" but has no readRecord.`,
      );
    }
  }
}

export function usePhiTableProvider(source: PhiProviderResourceSource | null) {
  const registry = useContext(PhiTableProviderRegistryContext);
  const provider = source ? registry.get(source.providerKey) ?? null : null;
  const resource = provider?.resources.find((candidate) =>
    candidate.resourceKey === source?.resourceKey) ?? null;

  return {
    provider,
    resource,
    bindingError: !source
      ? "Table data provider is not configured."
      : !provider
        ? `Table provider "${source.providerKey}" is not available from the active runtime modules.`
        : !resource
          ? `Table resource "${source.resourceKey}" is not declared by provider "${source.providerKey}".`
        : null,
  };
}
